import { useTranslation } from 'react-i18next';
import { S, Icon, StatusBadge, EmptyState, formatCHF, formatDate } from '../regureStyles';

const InvoicesTabContent = ({
  search,
  setSearch,
  setPage,
  statusFilter,
  setStatusFilter,
  STATUS_OPTIONS,
  STATUS_LABELS,
  filtered,
  paginated,
  handleMarkAsSent,
  handleRecordPayment,
  paying,
  onRowClick,
}) => {
  const { t } = useTranslation();
  const headers = [
    t('screens.invoicing.colInvoiceNumber'),
    t('screens.invoicing.colClient'),
    t('screens.invoicing.colPolicy'),
    t('screens.invoicing.colAmount'),
    t('screens.invoicing.colStatus'),
    t('screens.invoicing.colDueDate'),
    t('screens.invoicing.colProduct'),
    t('screens.invoicing.colAction'),
  ];
  return (
    <>
      {/* Filters */}
      <div
        style={{
          padding: '14px 32px',
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          borderBottom: `1px solid ${S.borderLight}`,
          background: 'rgba(255,255,255,0.5)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 380 }}>
          <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <Icon name='search' color='#94a3b8' size={15} />
          </div>
          <input
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={t('screens.invoicing.searchPlaceholder')}
            style={{
              width: '100%',
              padding: '9px 12px 9px 36px',
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              color: S.textPrimary,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '8px 12px',
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 8,
            fontSize: 13,
            color: S.textSecondary,
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          {STATUS_OPTIONS.map(o => (
            <option key={o} value={o}>
              {STATUS_LABELS[o]}
            </option>
          ))}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: S.textMuted }}>{t('screens.invoicing.resultsCount', { count: filtered.length })}</span>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px' }}>
        {paginated.length === 0 ? (
          <EmptyState icon='receipt' title={t('screens.invoicing.emptyInvoices')} />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {headers.map(h => (
                  <th
                    key={h}
                    style={{
                      padding: '13px 14px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: S.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      borderBottom: `2px solid ${S.borderLight}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((inv, i) => (
                <tr
                  key={inv.id}
                  style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard, transition: 'background 0.15s', cursor: onRowClick ? 'pointer' : 'default' }}
                  onClick={(e) => {
                    // Skip when the click originated on an action button
                    // (Mark as Sent / Record Payment) — those handlers
                    // stopPropagation isn't in reach, so filter by tag.
                    if (e.target.closest('button')) return;
                    if (onRowClick) onRowClick(inv._id);
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = S.bgHover)}
                  onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard)}
                >
                  <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 700, color: S.burgundy }}>{inv.id}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{inv.client}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{inv.policy}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{formatCHF(inv.amount)}</td>
                  <td style={{ padding: '13px 14px' }}>
                    <StatusBadge status={inv.status} />
                  </td>
                  <td
                    style={{
                      padding: '13px 14px',
                      fontSize: 12,
                      color: inv.status === 'overdue' ? '#ef4444' : S.textMuted,
                      fontWeight: inv.status === 'overdue' ? 600 : 400,
                    }}
                  >
                    {formatDate(inv.dueDate)}
                  </td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{inv.product}</td>
                  <td style={{ padding: '13px 14px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {inv.status === 'draft' && (
                      <button
                        onClick={() => handleMarkAsSent(inv)}
                        style={{
                          padding: '5px 12px',
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#2563eb',
                          cursor: 'pointer',
                        }}
                      >
                        {t('screens.invoicing.actionMarkAsSent')}
                      </button>
                    )}
                    {(inv.status === 'overdue' || inv.status === 'sent') && (
                      <button
                        onClick={() => handleRecordPayment(inv)}
                        disabled={paying === inv._id}
                        style={{
                          padding: '5px 12px',
                          background: S.bgCard,
                          border: `1px solid ${S.borderLight}`,
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          color: S.textSecondary,
                          cursor: 'pointer',
                        }}
                      >
                        {paying === inv._id ? t('screens.invoicing.saving') : t('screens.invoicing.actionRecordPayment')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default InvoicesTabContent;
